import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, clientApi, errMsg, getToken, getClientToken } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Camera, X, Plus, Printer, ArrowLeft, Save, Link as LinkIcon, Copy, MessageCircle, Car, Copy as CopyIcon, Trash2 } from "lucide-react";
import { checklistImageSrc } from "@/lib/checklistPhoto";

const SLOTS = [
  { key: "front", label: "Frente" },
  { key: "rear", label: "Traseira" },
  { key: "left", label: "Lateral esquerda" },
  { key: "right", label: "Lateral direita" },
  { key: "dashboard", label: "Painel de instrumentos" },
];

// Downscale + JPEG-encode the picked file so signatures/photos stay under
// a few hundred KB in the DB. The camera on phones easily produces 5MB+ JPEGs.
async function fileToCompressedDataURL(file, maxSide = 1600, quality = 0.75) {
  const url = URL.createObjectURL(file);
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = url;
  });
  const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  canvas.getContext("2d").drawImage(img, 0, 0, w, h);
  URL.revokeObjectURL(url);
  return canvas.toDataURL("image/jpeg", quality);
}

function SignaturePad({ value, onChange, label, testid, readOnly = false }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const setup = () => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    const w = c.offsetWidth;
    const h = 160;
    c.width = w * dpr;
    c.height = h * dpr;
    c.style.height = `${h}px`;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#111";
  };
  useEffect(() => { setup(); }, []);
  useEffect(() => {
    if (!value || !canvasRef.current) return;
    const img = new Image();
    // Storage-served signatures live on a different origin (backend) and the
    // canvas would taint without CORS. Emergent's proxy allows anonymous
    // cross-origin fetch, so this works for both legacy data URLs and the new
    // /api/uploads/{id}?auth=... URLs produced by checklistImageSrc.
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const ctx = canvasRef.current.getContext("2d");
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      ctx.drawImage(img, 0, 0, canvasRef.current.offsetWidth, 160);
    };
    const token = getToken() || getClientToken();
    img.src = checklistImageSrc(value, token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const pos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: p.clientX - rect.left, y: p.clientY - rect.top };
  };
  const start = (e) => { if (readOnly) return; e.preventDefault(); drawing.current = true; last.current = pos(e); };
  const move = (e) => {
    if (readOnly || !drawing.current) return;
    e.preventDefault();
    const p = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };
  const end = () => {
    if (readOnly || !drawing.current) return;
    drawing.current = false;
    onChange(canvasRef.current.toDataURL("image/png"));
  };
  const clear = () => {
    if (readOnly) return;
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    onChange("");
  };
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        {!readOnly && <Button type="button" size="sm" variant="outline" onClick={clear} data-testid={`${testid}-clear`}>Limpar</Button>}
      </div>
      <canvas
        data-testid={testid}
        ref={canvasRef}
        className={`w-full rounded-lg border border-border bg-white touch-none ${readOnly ? "pointer-events-none" : ""}`}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      />
      {!readOnly && <p className="text-xs text-muted-foreground">Assine no espaço acima usando o dedo/stylus.</p>}
    </div>
  );
}

function PhotoImg({ src, alt, className }) {
  // Auto-picks the admin or client token so `<img>` can auth against
  // /api/uploads/{id}. Legacy data URLs pass through unchanged.
  const token = getToken() || getClientToken();
  return <img src={checklistImageSrc(src, token)} alt={alt} className={className} onError={(e) => { e.currentTarget.style.opacity = 0.3; }} />;
}

function PhotoSlot({ dataUrl, onPick, onClear, label, testid, readOnly = false }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try { onPick(await fileToCompressedDataURL(f)); } catch { toast.error("Falha ao carregar imagem"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="space-y-1" data-testid={testid}>
      <div className="text-xs font-medium">{label}</div>
      {dataUrl ? (
        <div className="relative">
          <PhotoImg src={dataUrl} alt={label} className="w-full h-32 object-cover rounded-lg border border-border" />
          {!readOnly && (
            <button type="button" onClick={onClear} className="absolute top-1 right-1 bg-black/70 text-white rounded-full h-6 w-6 flex items-center justify-center" data-testid={`${testid}-remove`} aria-label="Remover">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ) : readOnly ? (
        <div className="w-full h-32 rounded-lg border border-dashed border-border flex items-center justify-center text-muted-foreground text-xs" data-testid={`${testid}-empty`}>Sem foto</div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()} className="w-full h-32 rounded-lg border-2 border-dashed border-border flex flex-col items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors" data-testid={`${testid}-pick`}>
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Camera className="h-6 w-6 mb-1" /><span className="text-xs">Tirar/Escolher foto</span></>}
        </button>
      )}
      <input ref={inputRef} type="file" accept="image/*" capture="environment" onChange={pick} className="hidden" />
    </div>
  );
}

export default function ChecklistPage({ mode = "admin" }) {
  const { aid } = useParams();
  const [sp] = useSearchParams();
  const readOnly = sp.get("view") === "1";
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [appt, setAppt] = useState(null);
  // Multi-checklist state: cada agendamento tem N slots (1 por veículo/quantidade
  // de serviços). `slots` é o array de forms indexado por slot_index-1.
  // `activeIdx` é qual aba está aberta. `nSlots` é a quantidade calculada a
  // partir do agendamento (sum das quantidades dos serviços).
  const emptyForm = () => ({
    slot_index: 1,
    photos: {}, extras: [],
    after_photos: {}, after_extras: [],
    damages: "",
    vehicle_plate: "", vehicle_model: "", vehicle_km: "",
    client_signature: "", tech_signature: "",
    tech_name: "", client_name: "",
  });
  const [slots, setSlots] = useState([{ ...emptyForm(), slot_index: 1 }]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [signLink, setSignLink] = useState({ open: false, url: "", loading: false });

  const form = slots[activeIdx] || slots[0];
  const setForm = (updater) => {
    setSlots((arr) => arr.map((s, i) => i === activeIdx ? (typeof updater === "function" ? updater(s) : { ...s, ...updater }) : s));
  };

  const apiClient = mode === "client" ? clientApi : api;
  const listUrl = mode === "client" ? `/client/appointments/${aid}/checklists` : `/appointments/${aid}/checklists`;

  const nSlots = useMemo(() => {
    if (!appt?.services?.length) return Math.max(1, slots.length);
    const total = appt.services.reduce((n, s) => n + (Number(s.quantity) || 1), 0);
    return Math.max(1, total);
  }, [appt, slots.length]);

  // Garantir que `slots` tenha pelo menos `nSlots` posições (preserva dados
  // já digitados nos slots existentes e cria vazios pros novos).
  useEffect(() => {
    setSlots((arr) => {
      if (arr.length >= nSlots) return arr;
      const extra = Array.from({ length: nSlots - arr.length }, (_, i) => ({ ...emptyForm(), slot_index: arr.length + i + 1 }));
      return [...arr, ...extra];
    });
  }, [nSlots]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // Carrega agendamento para conhecer serviços/quantidades.
        try {
          if (mode === "client") {
            const list = await clientApi.get("/client/appointments");
            setAppt((list.data || []).find((a) => a.id === aid) || null);
          } else {
            const list = await api.get("/appointments", { params: { day: undefined } });
            setAppt((list.data || []).find((a) => a.id === aid) || null);
          }
        } catch { /* seguir sem detalhes do agendamento */ }
        // Carrega TODOS os checklists do agendamento em uma chamada só.
        try {
          const { data } = await apiClient.get(listUrl);
          const arr = (data || []).map((c) => ({
            slot_index: c.slot_index || 1,
            photos: c.photos || {},
            extras: c.extras || [],
            after_photos: c.after_photos || {},
            after_extras: c.after_extras || [],
            damages: c.damages || "",
            vehicle_plate: c.vehicle_plate || "",
            vehicle_model: c.vehicle_model || "",
            vehicle_km: c.vehicle_km || "",
            client_signature: c.client_signature || "",
            tech_signature: c.tech_signature || "",
            tech_name: c.tech_name || "",
            client_name: c.client_name || "",
            signed_at: c.signed_at || null,
          }));
          if (arr.length) setSlots(arr);
        } catch { /* nenhum checklist ainda */ }
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aid, mode]);

  const setPhoto = (key, url) => setForm((f) => ({ ...f, photos: { ...f.photos, [key]: url } }));
  const setAfterPhoto = (key, url) => setForm((f) => ({ ...f, after_photos: { ...f.after_photos, [key]: url } }));
  const addExtra = async (file) => {
    const url = await fileToCompressedDataURL(file);
    setForm((f) => ({ ...f, extras: [...f.extras, { label: `Extra ${f.extras.length + 1}`, data_url: url }] }));
  };
  const removeExtra = (i) => setForm((f) => ({ ...f, extras: f.extras.filter((_, idx) => idx !== i) }));
  const addAfterExtra = async (file) => {
    const url = await fileToCompressedDataURL(file);
    setForm((f) => ({ ...f, after_extras: [...f.after_extras, { label: `Depois ${f.after_extras.length + 1}`, data_url: url }] }));
  };
  const removeAfterExtra = (i) => setForm((f) => ({ ...f, after_extras: f.after_extras.filter((_, idx) => idx !== i) }));

  // Duplica todos os dados do slot atual para o próximo slot vazio (útil quando
  // dois veículos têm a mesma condição inicial — o técnico só ajusta placa/KM).
  const duplicateToNext = () => {
    const nextIdx = slots.findIndex((s, i) => i > activeIdx && Object.keys(s.photos || {}).length === 0 && !s.vehicle_plate);
    const target = nextIdx >= 0 ? nextIdx : slots.length;
    setSlots((arr) => {
      const copy = { ...form, slot_index: target + 1 };
      if (target >= arr.length) return [...arr, copy];
      return arr.map((s, i) => i === target ? copy : s);
    });
    setActiveIdx(target);
    toast.success(`Dados copiados para Veículo ${target + 1}`);
  };

  const openSignLink = async () => {
    setSignLink({ open: true, url: "", loading: true });
    try {
      const { data } = await api.post(`/appointments/${aid}/checklist/sign-link`);
      const full = `${window.location.origin}${data.path}`;
      setSignLink({ open: true, url: full, loading: false });
    } catch (err) {
      setSignLink({ open: false, url: "", loading: false });
      toast.error(errMsg(err, "Falha ao gerar link"));
    }
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(signLink.url); toast.success("Link copiado"); }
    catch { toast.error("Copie manualmente o link"); }
  };
  const shareWhatsApp = () => {
    const msg = `Olá${appt?.client_name ? `, ${appt.client_name}` : ""}! Para confirmar o check-list do seu veículo, por favor assine no link abaixo:\n\n${signLink.url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/appointments/${aid}/checklist`, form, { params: { slot_index: form.slot_index || (activeIdx + 1) } });
      toast.success(`Check-list do Veículo ${activeIdx + 1} salvo`);
      if (form.client_signature && form.tech_signature) {
        // Só bloqueia edição (redireciona pra view) se TODOS os slots estão assinados.
        const allSigned = slots.every((s) => s.client_signature && s.tech_signature);
        if (allSigned) nav(`/checklist/${aid}?view=1`, { replace: true });
      }
    } catch (err) { toast.error(errMsg(err, "Falha ao salvar")); }
    finally { setSaving(false); }
  };

  const canSave = mode === "admin" && !readOnly;

  if (loading) return <div className="p-6 flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Carregando…</div>;

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-5 print:p-0">
      <div className="flex items-center gap-2 print:hidden">
        <Button variant="outline" size="sm" onClick={() => nav(-1)} data-testid="btn-back"><ArrowLeft className="h-4 w-4 mr-1" /> Voltar</Button>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="btn-print"><Printer className="h-4 w-4 mr-1" /> Imprimir</Button>
          {canSave && (
            <Button size="sm" onClick={save} disabled={saving} data-testid="btn-save-checklist">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Salvar
            </Button>
          )}
        </div>
      </div>

      <div className="surface p-5 space-y-1">
        <h1 className="font-display text-2xl font-bold">Check-list do veículo</h1>
        {appt && (
          <p className="text-sm text-muted-foreground">
            Agendamento #{appt.id?.slice(0, 8)} · {appt.client_name} · {appt.service_name}
          </p>
        )}
      </div>

      {/* Abas por veículo — uma pra cada quantidade de serviço no agendamento. */}
      {nSlots > 1 && (
        <div className="surface p-3 flex items-center gap-2 flex-wrap print:hidden" data-testid="checklist-tabs">
          {Array.from({ length: nSlots }).map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setActiveIdx(i)}
              data-testid={`tab-vehicle-${i + 1}`}
              className={`px-3 h-9 rounded-lg text-sm font-medium inline-flex items-center gap-1 border transition-colors
                ${activeIdx === i
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-muted text-muted-foreground border-border hover:border-primary hover:text-foreground"}`}
            >
              <Car className="h-3.5 w-3.5" /> Veículo {i + 1}
              {slots[i]?.vehicle_plate && <span className="ml-1 text-[10px] opacity-75">· {slots[i].vehicle_plate}</span>}
              {slots[i]?.signed_at && <span className="ml-1 text-[10px]">✓</span>}
            </button>
          ))}
          {!readOnly && mode === "admin" && (
            <Button variant="outline" size="sm" onClick={duplicateToNext} className="ml-auto" data-testid="btn-duplicate-checklist" title="Copia os dados deste veículo para o próximo slot">
              <CopyIcon className="h-4 w-4 mr-1" /> Duplicar dados
            </Button>
          )}
        </div>
      )}

      {nSlots > 1 && (
        <p className="text-xs text-muted-foreground print:hidden">Editando <strong>Veículo {activeIdx + 1}</strong> de {nSlots} · cada aba tem check-list independente (fotos, assinaturas e avarias próprias).</p>
      )}

      <div className="surface p-5 space-y-3">
        <h2 className="font-semibold">Identificação do veículo</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="space-y-1"><Label className="text-xs">Placa</Label><Input data-testid="input-plate" value={form.vehicle_plate} onChange={(e) => setForm({ ...form, vehicle_plate: e.target.value.toUpperCase() })} disabled={readOnly} /></div>
          <div className="space-y-1"><Label className="text-xs">Modelo</Label><Input data-testid="input-model" value={form.vehicle_model} onChange={(e) => setForm({ ...form, vehicle_model: e.target.value })} disabled={readOnly} /></div>
          <div className="space-y-1"><Label className="text-xs">KM</Label><Input data-testid="input-km" value={form.vehicle_km} onChange={(e) => setForm({ ...form, vehicle_km: e.target.value })} disabled={readOnly} /></div>
        </div>
      </div>

      <div className="surface p-5 space-y-3">
        <h2 className="font-semibold">Fotos obrigatórias</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {SLOTS.map((s) => (
            <PhotoSlot
              key={s.key}
              label={s.label}
              testid={`photo-${s.key}`}
              dataUrl={form.photos[s.key]}
              onPick={(url) => setPhoto(s.key, url)}
              onClear={() => setPhoto(s.key, "")}
              readOnly={readOnly}
            />
          ))}
        </div>
      </div>

      <div className="surface p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold">Fotos extras</h2>
          {!readOnly && (
            <label className="text-sm inline-flex items-center gap-2 cursor-pointer text-primary hover:underline">
              <Plus className="h-4 w-4" /> Adicionar
              <input type="file" accept="image/*" capture="environment" className="hidden" data-testid="input-extra" onChange={(e) => { const f = e.target.files?.[0]; if (f) addExtra(f); e.target.value = ""; }} />
            </label>
          )}
        </div>
        {form.extras.length === 0 && <p className="text-xs text-muted-foreground">Nenhuma foto extra adicionada.</p>}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {form.extras.map((x, i) => (
            <div key={i} className="relative" data-testid={`extra-${i}`}>
              <PhotoImg src={x.data_url} alt={x.label} className="w-full h-32 object-cover rounded-lg border border-border" />
              {!readOnly && (
                <button type="button" onClick={() => removeExtra(i)} className="absolute top-1 right-1 bg-black/70 text-white rounded-full h-6 w-6 flex items-center justify-center" data-testid={`extra-${i}-remove`}>
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="surface p-5 space-y-3">
        <h2 className="font-semibold">Avarias constatadas</h2>
        <Textarea data-testid="input-damages" rows={4} value={form.damages} onChange={(e) => setForm({ ...form, damages: e.target.value })} disabled={readOnly} placeholder="Ex.: risco pequeno na porta esquerda, retrovisor direito com trinca..." />
      </div>

      <div className="surface p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold">Fotos ao finalizar (depois)</h2>
          <span className="text-xs text-muted-foreground">Opcional · comparativo com a entrega</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {SLOTS.map((s) => (
            <PhotoSlot
              key={`after-${s.key}`}
              label={s.label}
              testid={`after-photo-${s.key}`}
              dataUrl={form.after_photos[s.key]}
              onPick={(url) => setAfterPhoto(s.key, url)}
              onClear={() => setAfterPhoto(s.key, "")}
              readOnly={readOnly}
            />
          ))}
        </div>
        <div className="flex items-center justify-between pt-2">
          <span className="text-sm font-medium">Fotos extras (depois)</span>
          {!readOnly && (
            <label className="text-sm inline-flex items-center gap-2 cursor-pointer text-primary hover:underline">
              <Plus className="h-4 w-4" /> Adicionar
              <input type="file" accept="image/*" capture="environment" className="hidden" data-testid="input-after-extra" onChange={(e) => { const f = e.target.files?.[0]; if (f) addAfterExtra(f); e.target.value = ""; }} />
            </label>
          )}
        </div>
        {form.after_extras.length === 0 && <p className="text-xs text-muted-foreground">Nenhuma foto "depois" adicionada.</p>}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {form.after_extras.map((x, i) => (
            <div key={i} className="relative" data-testid={`after-extra-${i}`}>
              <PhotoImg src={x.data_url} alt={x.label} className="w-full h-32 object-cover rounded-lg border border-border" />
              {!readOnly && (
                <button type="button" onClick={() => removeAfterExtra(i)} className="absolute top-1 right-1 bg-black/70 text-white rounded-full h-6 w-6 flex items-center justify-center" data-testid={`after-extra-${i}-remove`}>
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="surface p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-semibold">Assinaturas</h2>
          {mode === "admin" && !readOnly && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={openSignLink}
              data-testid="btn-remote-sign-link"
              className="print:hidden"
            >
              <LinkIcon className="h-4 w-4 mr-1" /> Enviar link para o cliente
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <SignaturePad
              testid="sig-client"
              label={`Assinatura do cliente${appt?.client_name ? ` (${appt.client_name})` : ""}`}
              value={form.client_signature}
              onChange={(v) => !readOnly && setForm({ ...form, client_signature: v })}
              readOnly={readOnly}
            />
          </div>
          <div>
            <SignaturePad
              testid="sig-tech"
              label="Assinatura do técnico"
              value={form.tech_signature}
              onChange={(v) => !readOnly && setForm({ ...form, tech_signature: v })}
              readOnly={readOnly}
            />
          </div>
        </div>
        {form.signed_at && (
          <p className="text-xs text-muted-foreground">Assinado em {new Date(form.signed_at).toLocaleString("pt-BR")}</p>
        )}
      </div>

      {canSave && (
        <div className="print:hidden">
          <Button className="w-full h-12" onClick={save} disabled={saving} data-testid="btn-save-checklist-bottom">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Salvar check-list
          </Button>
        </div>
      )}

      <Dialog open={signLink.open} onOpenChange={(v) => setSignLink((s) => ({ ...s, open: v }))}>
        <DialogContent data-testid="dialog-sign-link">
          <DialogHeader>
            <DialogTitle>Link de assinatura remota</DialogTitle>
            <DialogDescription>
              Compartilhe este link com o cliente por WhatsApp, SMS ou e-mail. Ele conseguirá assinar diretamente do celular, sem precisar estar presente. O link expira em 7 dias.
            </DialogDescription>
          </DialogHeader>
          {signLink.loading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-4"><Loader2 className="h-4 w-4 animate-spin" /> Gerando link…</div>
          ) : (
            <div className="space-y-3">
              <div className="rounded-lg bg-muted p-3 break-all text-sm font-mono" data-testid="sign-link-url">{signLink.url}</div>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" onClick={copyLink} data-testid="btn-copy-sign-link"><Copy className="h-4 w-4 mr-1" /> Copiar</Button>
                <Button onClick={shareWhatsApp} data-testid="btn-whatsapp-sign-link"><MessageCircle className="h-4 w-4 mr-1" /> WhatsApp</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
